import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { useState } from 'react'
import { Values } from '../../Types'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Formik } from 'formik'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { AlmanacFile } from 'types'
import { Form } from 'components/Wizards/Form/Form'
import { Field } from 'components/Wizards/Field/Field'
import { useFormikContext } from 'formik'
import { Button } from 'blaze-ui/Button/Button'
import { useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { useUploads } from 'components/Design/UploadsTab/useUploads'
import { collectResults } from 'utils/query'
import { getMediaFiles } from 'components/Design/UploadsTab/getMediaFiles'
import Styles from './UploadVideo.module.scss'
import { ACCEPTED_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { Carousel } from 'components/Carousel/Carousel'
import { VideoTile } from './components/VideoTile'
import { VideoDropzone } from './components/VideoDropzone'
import { AllUploads } from 'components/ImagePickerModal/Uploads/AllUploads'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { getVideoMimeType } from 'components/Design/UploadsTab/components/StockVideosGallery'
import { v4 as uuidv4 } from 'uuid'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { useStockVideos } from 'hooks/queries/designer/useStockVideos'
import { useStockVideoDownload } from 'hooks/queries/designer/useStockVideoDownload'

interface Props extends StepProps {
  createDesign: (values: Values) => Promise<void>
  isDownloading: boolean
  setIsDownloading: (val: boolean) => void
  uploadVideo: (files: File[], projectId?: string) => Promise<AlmanacFile[] | undefined>
  setSelectedMediaFile: (file: IMediaFile) => void
}

const PAGE_SIZE = 50
const LOADING_HEIGHT = 180
const LOADING_WIDTH = 101
const LOADING_ASPECT_RATIO = LOADING_WIDTH / LOADING_HEIGHT

export const UploadVideo = multiStep<Props>((props) => {
  const { schema, onSubmit, initialValues } = props

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      <Content {...props} />
    </Formik>
  )
})

const Content = (props: Props) => {
  const { createDesign, uploadVideo, isDownloading, setIsDownloading, setSelectedMediaFile, onSubmit } = props
  const { openModal, closeModal } = useModals()
  const uploadsQuery = useUploads()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(currentWorkspaceId).data?.project?.id
  const { isSubmitting, initialValues } = useFormikContext<Values>()
  const [needsDownload, setNeedsDownload] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState<IMediaFile | null>(null)

  const uploads = collectResults(uploadsQuery, (data) => data.files)
  const mediaFiles = getMediaFiles(uploads, ACCEPTED_VIDEO_MIME_TYPES)

  const {data, isLoading: stockVideosLoading} = useStockVideos({
    query: '',
    pageSize: PAGE_SIZE,
    videoType: 'all',
  })
  
  const { downloadHighRes } = useStockVideoDownload()

  const stockVideos = data?.pages
    .flatMap((page) => page.videos)
    .map((video) => {
      const mediumVideo = video.videos.medium
      return {
        id: video.id,
        source_url: mediumVideo.url,
        mime_type: mediumVideo.mime_type ?? getVideoMimeType(mediumVideo.url),
        width: mediumVideo.width,
        height: mediumVideo.height,
        duration: video.duration,
        variants: [],
        thumbnail_url: mediumVideo.thumbnail,
      }
    })

  const handleOpenAll = () => {
    openModal(
      AllUploads,
      {
        accept: ACCEPTED_VIDEO_MIME_TYPES,
        confirmLabel: 'Use Video',
        onPressOutside: closeModal,
        onConfirm: (videos) => {
          const video = videos[0]
          if (!video || !video.source_url || !video.id) return
          setSelectedVideo(video as IMediaFile)
          closeModal()
        },
        uploadImages: async () => {
          return []
        },
      },
      { newStack: true },
    )
  }

  const handleStartWithBlankCanvas = async () => {
    await createDesign({
      ...initialValues,
      filename: 'Blank Canvas',
    })
  }

  const uploadFile = async (file: File) => {
    const uploadedFiles = await uploadVideo([file], uploadsProjectId)
    if (!uploadedFiles) return
    const uploadedFile = uploadedFiles[0]
    return uploadedFile
  }

  const fetchAndUploadVideo = async (url: string, dismissToast: () => void, mimeType: string) => {
    setIsDownloading(true)
    const originalFile = await fetch(url)
    const blob = await originalFile.blob()
    const [, extension] = mimeType.split('/')
    const file = new File([blob], `${uuidv4()}.${extension}`, { type: mimeType })
    dismissToast()
    return await uploadFile(file)
  }

  const handleSubmit = async () => {
    if (!selectedVideo) return

    if (needsDownload) {
      const dismissToast = showToastMessage('Processing video...', 'loading', undefined, 0)
      
      try {
        const highResUrl = await downloadHighRes(selectedVideo.id)       
        const updatedVideo = {
          ...selectedVideo,
          source_url: highResUrl || selectedVideo.source_url
        }
        setSelectedVideo(updatedVideo)

        const file = await fetchAndUploadVideo(updatedVideo.source_url, dismissToast, selectedVideo.mime_type)
        if (!file) return
        setSelectedMediaFile(file as IMediaFile)
        onSubmit({ initial_media_file_id: file.id })
      } catch (error) {
        dismissToast()
        showErrorMessageToast('Something went wrong while downloading your video. Please try again.')
        reportError(error)
      } finally {
        setIsDownloading(false)
      }
    } else {
      setSelectedMediaFile(selectedVideo)
      onSubmit({ initial_media_file_id: selectedVideo.id })
    }
  }

  const onVideoPress = (video: IMediaFile, needsDownload: boolean) => {
    setSelectedVideo(video)
    setNeedsDownload(needsDownload)
  }

  const onDrop = async (files: File[]) => {
    const uploadedFile = await uploadFile(files[0])
    if (uploadedFile && uploadedFile.source_url) {
      setSelectedVideo(uploadedFile as IMediaFile)
    }
  }

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <div className={Styles.container}>
            <div className={Styles.containerLeft}>
              <VideoDropzone onDrop={onDrop} />
            </div>

            <div className={Styles.containerRight}>
              {(!!mediaFiles.length || uploadsQuery.isLoading) && (
                <div className={Styles.containerSection}>
                  <div className={Styles.heading}>
                    <Text variant="largeList">Use recently uploaded media</Text>
                    <Button variant="tertiary" size="sm" onPress={handleOpenAll}>
                      Media Library
                    </Button>
                  </div>
                  <div className={Styles.mediaFiles}>
                    <Carousel slideGap={12}>
                      {uploadsQuery.isLoading &&
                        Array.from({ length: 4 }).map((_, i) => (
                          <div
                            className={Styles.mediaFileContainer}
                            key={`loader-${i}`}
                            data-testid={`upload-loader-${i}`}
                          >
                            <ShimmeringColor
                              aspectRatio={LOADING_ASPECT_RATIO}
                              width={LOADING_WIDTH}
                              height={LOADING_HEIGHT}
                              key={i}
                            />
                          </div>
                        ))}
                      {mediaFiles.map((video) => (
                        <VideoTile
                          key={video.id}
                          video={video}
                          onVideoPress={onVideoPress}
                          isSelected={selectedVideo?.id === video.id}
                        />
                      ))}
                    </Carousel>
                  </div>
                </div>
              )}
              <div className={Styles.containerSection}>
                <div className={Styles.heading}>
                  <Text variant="largeList">Start with a stock video</Text>
                </div>
                <div className={Styles.mediaFiles}>
                  <Carousel slideGap={12}>
                    {stockVideosLoading &&
                      Array.from({ length: 4 }).map((_, i) => (
                        <div className={Styles.mediaFileContainer} key={`stock-loader-${i}`}>
                          <ShimmeringColor
                            aspectRatio={LOADING_ASPECT_RATIO}
                            width={LOADING_WIDTH}
                            height={LOADING_HEIGHT}
                            key={i}
                          />
                        </div>
                      ))}
                    {stockVideos?.map((stockVideo) => (
                      <VideoTile
                        key={stockVideo.id}
                        video={stockVideo}
                        isSelected={selectedVideo?.id === stockVideo.id}
                        onVideoPress={onVideoPress}
                        needsDownload
                      />
                    ))}
                  </Carousel>
                </div>
              </div>
            </div>
          </div>
          <Form.Fields>
            <Field name="video" />
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>

      <Form.Actions isSticky>
        <Form.PrimaryActions>
          <div className={Styles.footerButtonsRight}>
            <Button
              variant="tertiary"
              size="lg"
              onPress={handleStartWithBlankCanvas}
              isDisabled={isSubmitting || isDownloading}
            >
              Start With a Blank Canvas
            </Button>
            <Button
              variant="primary"
              size="lg"
              onPress={handleSubmit}
              isDisabled={isSubmitting || !selectedVideo || isDownloading}
            >
              {needsDownload ? 'Upload Video' : 'Use Selected Video'}
            </Button>
          </div>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}
